import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';

const API_BASE_URL = process.env.NODE_ENV === 'production'
  ? 'https://healthcare.visionkraftconsulting.com/api'
  : 'http://localhost:3025/api';

const ClientManagementPage = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone_number: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    hourly_rate: ''
  });

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/clients/unified`);
      // Handle both array response and object response
      const clientData = Array.isArray(response.data) ? response.data : (response.data.clients || []);
      
      // Add bill_to_name handling for display
      const processedClients = clientData.map(client => ({
        ...client,
        display_name: client.first_name || client.last_name 
          ? `${client.first_name || ''} ${client.last_name || ''}`.trim()
          : client.bill_to_name || 'Unknown Client'
      }));
      
      setClients(processedClients);
    } catch (err) {
      console.error('Failed to fetch clients:', err);
      setError('Failed to load clients');
      setClients([]); // Set empty array on error
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingClient) {
        // Update existing client
        await axios.put(`${API_BASE_URL}/clients/unified/${editingClient.id}`, formData);
        setSuccessMessage('Client updated successfully');
      } else {
        // Create new client
        await axios.post(`${API_BASE_URL}/clients/unified`, formData);
        setSuccessMessage('Client created successfully');
      }
      
      setShowForm(false);
      setEditingClient(null);
      setFormData({
        first_name: '',
        last_name: '',
        email: '',
        phone_number: '',
        address: '',
        city: '',
        state: '',
        zip_code: '',
        hourly_rate: ''
      });
      fetchClients();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Failed to save client:', err);
      setError('Failed to save client');
      setTimeout(() => setError(''), 3000);
    }
  };

  const handleEdit = (client) => {
    setEditingClient(client);
    setFormData({
      first_name: client.first_name || '',
      last_name: client.last_name || '',
      email: client.email || '',
      phone_number: client.phone_number || '',
      address: client.address || '',
      city: client.city || '',
      state: client.state || '',
      zip_code: client.zip_code || '',
      hourly_rate: client.hourly_rate || ''
    });
    setShowForm(true);
  };

  const handleDelete = async (clientId) => {
    if (!window.confirm('Are you sure you want to delete this client?')) {
      return;
    }

    try {
      await axios.delete(`${API_BASE_URL}/clients/unified/${clientId}`);
      setSuccessMessage('Client deleted successfully');
      fetchClients();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Failed to delete client:', err);
      setError('Failed to delete client');
      setTimeout(() => setError(''), 3000);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingClient(null);
    setFormData({
      first_name: '',
      last_name: '',
      email: '',
      phone_number: '',
      address: '',
      city: '',
      state: '',
      zip_code: '',
      hourly_rate: ''
    });
  };

  if (loading) {
    return <div className="container"><p>Loading clients...</p></div>;
  }

  return (
    <div className="container">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Client Management</h2>
        <div>
          <button 
            className="btn btn-primary me-2"
            onClick={() => setShowForm(true)}
          >
            <i className="bi bi-plus-circle me-2"></i>
            Add New Client
          </button>
          <Link to="/invoice" className="btn btn-outline-primary">
            <i className="bi bi-receipt me-2"></i>
            Generate Invoice
          </Link>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger">{error}</div>
      )}
      
      {successMessage && (
        <div className="alert alert-success">{successMessage}</div>
      )}

      {showForm && (
        <div className="card mb-4">
          <div className="card-header">
            <h5>{editingClient ? 'Edit Client' : 'Add New Client'}</h5>
          </div>
          <div className="card-body">
            <form onSubmit={handleSubmit}>
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label htmlFor="first_name" className="form-label">First Name *</label>
                  <input
                    type="text"
                    className="form-control"
                    id="first_name"
                    name="first_name"
                    value={formData.first_name}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="col-md-6 mb-3">
                  <label htmlFor="last_name" className="form-label">Last Name *</label>
                  <input
                    type="text"
                    className="form-control"
                    id="last_name"
                    name="last_name"
                    value={formData.last_name}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label htmlFor="email" className="form-label">Email</label>
                  <input
                    type="email"
                    className="form-control"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                  />
                </div>
                <div className="col-md-6 mb-3">
                  <label htmlFor="phone_number" className="form-label">Phone Number</label>
                  <input
                    type="text"
                    className="form-control"
                    id="phone_number"
                    name="phone_number"
                    value={formData.phone_number}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
              <div className="mb-3">
                <label htmlFor="address" className="form-label">Address</label>
                <input
                  type="text"
                  className="form-control"
                  id="address"
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                />
              </div>
              <div className="row">
                <div className="col-md-4 mb-3">
                  <label htmlFor="city" className="form-label">City</label>
                  <input
                    type="text"
                    className="form-control"
                    id="city"
                    name="city"
                    value={formData.city}
                    onChange={handleInputChange}
                  />
                </div>
                <div className="col-md-4 mb-3">
                  <label htmlFor="state" className="form-label">State</label>
                  <input
                    type="text"
                    className="form-control"
                    id="state"
                    name="state"
                    value={formData.state}
                    onChange={handleInputChange}
                  />
                </div>
                <div className="col-md-4 mb-3">
                  <label htmlFor="zip_code" className="form-label">ZIP Code</label>
                  <input
                    type="text"
                    className="form-control"
                    id="zip_code"
                    name="zip_code"
                    value={formData.zip_code}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
              <div className="mb-3">
                <label htmlFor="hourly_rate" className="form-label">Hourly Rate ($)</label>
                <input
                  type="number"
                  step="0.01"
                  className="form-control"
                  id="hourly_rate"
                  name="hourly_rate"
                  value={formData.hourly_rate}
                  onChange={handleInputChange}
                />
              </div>
              <div className="d-flex gap-2">
                <button type="submit" className="btn btn-primary">
                  {editingClient ? 'Update Client' : 'Add Client'}
                </button>
                <button type="button" className="btn btn-secondary" onClick={handleCancel}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {clients.length === 0 ? (
        <div className="alert alert-info">
          No clients found. Add your first client to get started.
        </div>
      ) : (
        <div className="table-responsive">
          <table className="table table-striped table-hover">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Address</th>
                <th>Hourly Rate</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {clients.map((client) => (
                <tr key={client.id}>
                  <td>{client.display_name}</td>
                  <td>{client.email}</td>
                  <td>{client.phone_number}</td>
                  <td>
                    {client.address && `${client.address}, `}
                    {client.city && `${client.city}, `}
                    {client.state} {client.zip_code}
                  </td>
                  <td>{client.hourly_rate ? `$${client.hourly_rate}` : 'N/A'}</td>
                  <td>
                    <button
                      onClick={() => handleEdit(client)}
                      className="btn btn-sm btn-outline-primary me-1"
                      title="Edit"
                    >
                      <i className="bi bi-pencil"></i>
                    </button>
                    <button
                      onClick={() => handleDelete(client.id)}
                      className="btn btn-sm btn-outline-danger"
                      title="Delete"
                    >
                      <i className="bi bi-trash"></i>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ClientManagementPage;